// cinematic/narrate.ts (CX3) - the pro's voice, in the moment. The real selection + prose layer that
// replaces the demo `demoBeats` in cinematic/main.tsx: given a played hand's frame stream (CX1) and its
// LiveHandModel (the engine's per-decision recommendation + the scripted per-seat archetypes), it returns
// the `Record<number, Beat>` the CinematicFelt renders - keyed by frame ordinal `i`, one self-replacing
// bubble synced to the frame.
//
// THREE things it does, all three the CX3 objective (BUILD_PLAN §CX3):
//   1. SELECTION. The pro speaks only on beats that matter. Hero's own decisions and meaningful VILLAIN
//      aggression (a bet / raise / jam) get a beat; villain calls, checks and FOLDS stay SILENT (a missing
//      key - the felt shows "(watching)"). This is the fix for "every action narrated, none of it lands".
//   2. WORD BUDGET (research/08b). A villain read / opening / result beat is 8–15 words; hero's real
//      decision gets up to ~25. Enforced by a test (narrate.test.ts), authored to fit here.
//   3. REAL, ADAPTIVE, IN-THE-MOMENT PROSE (North Star §4.1: the engine decides, the pro only explains).
//      A hero beat traces to the engine's recommendation + THIS villain (`snapshot.recommendation` +
//      `snapshot.villainArchetypeId`); a villain beat traces to the seat's SCRIPTED archetype record via
//      the same predicate vocabulary the folded-hand read uses (bluffBandOf / isStation / … from
//      foldedObservation.ts). So the SAME spot vs a station vs a nit produces DIFFERENT words - because
//      the read keys on who the man actually is, never on a template.
//
// THE FIREWALL (North Star §4.1). Villain beats reference the seat's ARCHETYPE and its scripted range shape
// - never a hole card. The only cards this layer can name are HERO's own (shown on the felt's hero dock) and
// the showdown cards the engine minted on the award frame. A villain's hole cards never enter here.
import type { ArchetypeId, CardIndex } from '@postflop/schema';
import type { Beat } from '../components/CinematicFelt';
import type { Lens } from './lens';
import type { AwardFrame, HandFrame, HandStream } from '../engine/frameStream';
import { money } from '../engine/bridge';
import { decodeCard } from '../engine/cards';
import { archetypeOf } from '../engine/liveData';
import { displayName, type LiveHandModel, type LiveSnapshot } from '../engine/liveRunner';
import {
  bluffBandOf,
  isStation,
  overBluffs,
  overFolds,
  overvaluesPairs,
  tooPolar,
} from '../live/foldedObservation';

const wordsIn = (s: string): number => s.trim().split(/\s+/).filter(Boolean).length;

// ── hero hand label (hero's OWN cards, shown on the felt - not firewalled) ─────────────────────────────
const RANK_WORD: Record<string, string> = {
  A: 'aces', K: 'kings', Q: 'queens', J: 'jacks', T: 'tens',
  '9': 'nines', '8': 'eights', '7': 'sevens', '6': 'sixes', '5': 'fives', '4': 'fours', '3': 'threes', '2': 'twos',
};

/** "ace-king suited", "pocket tens", "queen-seven offsuit" - hero's two cards in the pro's spoken shorthand. */
export function heroHandLabel(cards: readonly [CardIndex, CardIndex]): string {
  const a = decodeCard(cards[0]);
  const b = decodeCard(cards[1]);
  if (a.rank === b.rank) return `pocket ${RANK_WORD[a.rank] ?? `${a.rank}s`}`;
  const [hi, lo] = rankOrder(a.rank) >= rankOrder(b.rank) ? [a, b] : [b, a];
  const suited = a.glyph === b.glyph ? 'suited' : 'offsuit';
  return `${nameRank(hi.rank)}-${nameRank(lo.rank)} ${suited}`;
}
const RANK_SEQ = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
const rankOrder = (r: string): number => RANK_SEQ.indexOf(r);
const nameRank = (r: string): string =>
  ({ A: 'ace', K: 'king', Q: 'queen', J: 'jack', T: 'ten' } as Record<string, string>)[r] ?? r;

// ── the villain read vocabulary, terse (≤15 words), keyed on the scripted archetype ────────────────────

/** A short trait phrase for a villain, used INSIDE a hero beat ("the Whale never folds a pair"). Third
 *  person, present tense, no cards. Keyed on the archetype's own scripted axes - un-invented. */
export function villainTrait(id: ArchetypeId): string {
  const a = archetypeOf(id);
  if (isStation(a)) return overvaluesPairs(a) ? 'calls with any pair' : 'never folds once he is in';
  if (overFolds(a)) return 'folds unless he has it';
  if (overBluffs(a) || bluffBandOf(a) === 'often') return 'fires with almost anything';
  if (bluffBandOf(a) === 'never') return 'does not bluff';
  return 'plays it fairly straight';
}

/**
 * The pro's terse read of ONE villain action, for the in-the-moment bubble (≤15 words). Only meaningful
 * aggression reaches here (bet / raise / jam - the selection layer keeps calls, checks and folds silent).
 * The read splits hardest on the archetype, exactly like the folded-hand `postflopReadFor`, but said in one
 * breath instead of a paragraph - and self-contained (no "over {name}"): the felt already shows who acted
 * and whom he raised, so the budget goes to the READ, not the bookkeeping. The SAME action from a station
 * and a nit returns DIFFERENT words, because the read keys on who the man is (North Star §4.1).
 */
export function villainMoment(id: ArchetypeId, kind: string, ctx: { isReraise: boolean }): string {
  const a = archetypeOf(id);
  const name = displayName(id);

  // a raise / check-raise - the loudest action, where the profiles split hardest.
  if (ctx.isReraise || kind === 'raise' || kind === 'jam') {
    if (isStation(a)) return `${name} raising is wildly off profile - a passive player has a real hand.`;
    if (overFolds(a)) return `${name} raises. He folds most hands, so this is the top of his range.`;
    if (bluffBandOf(a) === 'often') return `${name} raises - he does this with anything, so it says little.`;
    return `${name} raises. Balanced player: mostly a real hand, a few bluffs mixed in.`;
  }

  // taking the lead - the first bet of the street.
  if (isStation(a)) return `${name} leads out, which he rarely does - passive players bet when good.`;
  if (overFolds(a)) return `${name} bets. He does not fire light, so read it as a real hand.`;
  if (bluffBandOf(a) === 'never') return `${name} leads out. He does not bluff, so I believe that bet.`;
  if (bluffBandOf(a) === 'often') return `${name} fires again - he bets with anything, so it barely narrows him.`;
  if (tooPolar(a)) return `${name} bets big. His range here is polar - the nuts or pure air.`;
  return `${name} takes the lead. On profile: some value, some air, nothing to exploit yet.`;
}

// ── hero's own decisions (up to ~25 words) ─────────────────────────────────────────────────────────────

type PostflopStep = Extract<LiveSnapshot, { kind: 'postflop' }>;
type PreflopStep = Extract<LiveSnapshot, { kind: 'preflop' }>;

/** The pro narrating his OWN postflop decision: what he has, the WHY (this villain / the engine's fired
 *  read / the price), and the action he takes. Traces to `snapshot.recommendation` + `villainArchetypeId`
 *  (North Star §4.1). ≤~25 words. */
function heroPostflopBeat(step: PostflopStep, kind: string, amount: number): string {
  const handClass = step.digest.hero.handClass.replace(/_/g, ' ');
  const villain = villainTrait(step.villainArchetypeId);
  const vname = displayName(step.villainArchetypeId);
  // is there a real exploit read driving this? (a fired rule the engine did NOT suppress).
  const exploit = step.recommendation.firedRules.some((r) => !r.suppressed);

  if (kind === 'fold') {
    return `Just ${handClass}, and ${vname} ${villain}. Nothing to do here - I let it go.`;
  }
  if (kind === 'check') {
    // vary the check by street so a check-down does not repeat one sentence three times.
    const tail =
      step.street === 'river'
        ? `so I check it back and get to showdown cheaply.`
        : step.street === 'turn'
          ? `so I check for pot control and keep my options open.`
          : `so I check and keep the pot small against ${vname}.`;
    return `${cap(handClass)}. Not strong enough to bet here, ${tail}`;
  }
  if (kind === 'call') {
    return `${cap(handClass)}, and the price is right against ${vname} - I call ${money(amount)} to see more.`;
  }
  // bet / raise / jam - hero applies pressure.
  if (exploit) {
    return `${cap(handClass)}, and ${vname} ${villain}. That is the read - I bet ${money(amount)} to make him pay.`;
  }
  return `${cap(handClass)} and I am ahead of ${vname}'s range here, so I bet ${money(amount)} for value.`;
}

/** The pro narrating his OWN preflop decision: the hand, the seat's logic, the action. ≤~25 words. */
function heroPreflopBeat(step: PreflopStep, stream: HandStream, kind: string, amount: number): string {
  const hand = heroHandLabel(stream.heroCards);
  const vname = displayName(step.villainArchetypeId);
  if (kind === 'fold') return `${cap(hand)}, and there is nothing here worth playing. I fold and wait.`;
  if (kind === 'check') return `${cap(hand)} in the big blind. No reason to raise - I check and take a free flop.`;
  if (kind === 'call') return `${cap(hand)}. Good enough to see a flop cheaply against ${vname}, so I call ${money(amount)}.`;
  return `${cap(hand)}. Strong enough to take the lead - I raise to ${money(amount)} and put ${vname} to a choice.`;
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// ── the opening + the result ───────────────────────────────────────────────────────────────────────────

const OPENING = `I'm in this one. Let me watch how the table moves before I commit.`;

function awardBeat(frame: AwardFrame, stream: HandStream): string {
  const { award } = frame;
  const labelOf = (seat: number): string =>
    seat === stream.heroSeat ? 'me' : stream.seats.find((s) => s.seat === seat)?.label ?? `seat ${seat}`;
  const who = award.winners.map(labelOf).join(' and ');
  const showdown = award.shown.length > 1;
  return showdown
    ? `${money(award.amount)} to ${who} at showdown. That is the hand.`
    : `${money(award.amount)} to ${who} - taken down without a showdown.`;
}

// ── the selection driver: walk the frames, decide who speaks ────────────────────────────────────────────

/**
 * Build the frame-synced narration for a whole played hand. Pure: same (stream, model, lens) → same beats.
 *
 * Selection rule (the heart of CX3): frame 0 opens the read; a HERO action always speaks (his decision is
 * the whole point); a VILLAIN bet/raise/jam speaks (a read on the man); everything else - villain calls,
 * checks, folds, the sweep/board-deal beats - stays SILENT (no key). The award frame closes the hand.
 *
 * The `lens` (CX4) is the per-mode config over that ONE selection rule - not a second narrator:
 *   - `observe` (default): the full transcript above. The pro plays hero's line and narrates all of it,
 *     including his own decisions - this is the flagship "watch a pro play + explain" experience.
 *   - `diagnose`: hero is the one deciding, so the pro does NOT pre-narrate hero's decisions (that would put
 *     words in hero's mouth); the opening self-narration is dropped too. What survives is the VILLAIN reads
 *     and the closing verdict - the in-the-moment table read, with hero's own coaching coming from the grade.
 *   - `solo`: silent live (narration is held to the debrief, North Star §5) - an empty map; the felt reads
 *     "(watching)" and the pro's read of the whole hand is replayed with the SAME narrator (observe lens)
 *     in the end-of-hand report.
 */
export function narrateHand(
  stream: HandStream,
  model: LiveHandModel,
  opts: { lens?: Lens } = {},
): Record<number, Beat> {
  const lens: Lens = opts.lens ?? 'observe';
  const out: Record<number, Beat> = {};
  // Solo holds all narration for the debrief - nothing speaks live.
  if (lens === 'solo') return out;
  const narrateHero = lens === 'observe'; // only the pro-plays lens speaks hero's own decisions.
  const heroSeat = stream.heroSeat;

  // hero's decision frames map, in order, to his steps (preflop nodes then postflop snapshots).
  const steps = model.steps;
  let stepIdx = 0;

  // track the current street's aggressor so a villain bet reads as an open vs a re-raise.
  let street = stream.frames[0]?.street;
  let aggressor: number | null = null;

  stream.frames.forEach((frame: HandFrame, i) => {
    if (frame.street !== street) {
      street = frame.street;
      aggressor = null; // a new street: the lead is open again.
    }

    if (i === 0) {
      // the opening is the pro self-narrating that he is in the hand - only in the pro-plays lens.
      if (narrateHero) out[i] = { text: OPENING };
      return;
    }

    if (frame.phase === 'award') {
      out[i] = { text: awardBeat(frame as AwardFrame, stream) };
      return;
    }

    const a = frame.action;
    if (!a) return; // synthesized sweep frame with no action - silent.

    const isAggression = a.kind === 'bet' || a.kind === 'raise' || a.kind === 'jam';

    if (a.seat === heroSeat) {
      // hero's own decision. Consume the matching step (keeps the villain-aggressor bookkeeping and the
      // step alignment correct in EVERY lens) but only SPEAK it in the pro-plays lens - in the do-modes hero
      // makes this call himself, so the pro does not narrate it (his coaching is the grade instead).
      const step = steps[stepIdx];
      if (stepIdx < steps.length) stepIdx += 1;
      if (narrateHero) {
        const text = step
          ? step.kind === 'preflop'
            ? heroPreflopBeat(step, stream, a.kind, a.amount)
            : heroPostflopBeat(step, a.kind, a.amount)
          : heroFallbackBeat(a.kind, a.amount, stream);
        // CX9: a hero beat often names the villain (vname); carry his archetype so the bubble makes that
        // name tap-to-learn. beatNodes no-ops when the beat did not name him (e.g. a preflop fold).
        out[i] = { decision: true, text, archetypeId: step?.villainArchetypeId };
      }
      if (isAggression) aggressor = a.seat;
      return;
    }

    // a villain acted. Only aggression is a beat that matters; calls / checks / folds are silent.
    if (isAggression) {
      const id = archetypeIdOf(model, a.seat);
      if (id) {
        const text = villainMoment(id, a.kind, { isReraise: aggressor !== null });
        // CX9: a villain moment always names the type; carry the id so the bubble makes it tap-to-learn.
        if (text) out[i] = { text, archetypeId: id };
      }
      aggressor = a.seat;
    }
  });

  return out;
}

/** the scripted archetype for a seat (the Axis-B ground truth), or undefined for a seat with none. */
function archetypeIdOf(model: LiveHandModel, seat: number): ArchetypeId | undefined {
  return model.terminal.players.find((p) => p.seat === seat)?.archetypeId;
}

/** hero acted but there is no step to read from (defensive - the two lists should align). Still a real beat,
 *  just without the engine's why. */
function heroFallbackBeat(kind: string, amount: number, stream: HandStream): string {
  const hand = heroHandLabel(stream.heroCards);
  if (kind === 'fold') return `${cap(hand)} - not my spot. I let this one go and keep my stack.`;
  if (kind === 'check') return `I check ${hand} and keep the pot manageable for now.`;
  if (kind === 'call') return `I call ${money(amount)} with ${hand} and see what comes.`;
  return `I make it ${money(amount)} with ${hand} and take the initiative.`;
}

// exported for the word-budget test.
export { wordsIn };
